/**
 * File: contractImport.ts
 * Path: packages/config/src/contracts/contractImport.ts
 * Description: Bringing in a contract that was already signed somewhere else
 * (Zoho Contracts, Google Docs e-signature). The old signed PDF is the proof
 * of signature, so nothing is sent and nobody signs again. Two kinds can be
 * brought in: a once-off service agreement and a retainer.
 *
 * The same checks run in the admin form and on the server before anything
 * is written.
 */

import { southAfricanDay } from "./contractDue";
import { isIsoDate } from "./contractFormat";
import type { ContractContent, ContractStage } from "./contractTypes";

/** Templates an old contract can come in as. */
export const CONTRACT_IMPORT_TEMPLATES = ["service", "retainer"] as const;

/** The old signed PDF: it is stored as it is, so keep the upload modest. */
export const IMPORT_PDF_MAX_BYTES = 6 * 1024 * 1024;

/** 12:00 in South Africa (10:00 UTC) on the day it was signed, so the day never shifts. */
export const signedOnToMs = (signedOn: string): number => Date.parse(`${signedOn}T10:00:00Z`);

export interface ContractImportDraft {
  content: ContractContent;
  signedOn: string;
  clientSignerName: string;
  providerSignerName: string;
}

/** What is still missing or wrong; empty when it can be brought in. */
export const contractImportIssues = (draft: ContractImportDraft, nowMs: number): string[] => {
  const { content } = draft;
  const issues: string[] = [];
  if (!(CONTRACT_IMPORT_TEMPLATES as readonly string[]).includes(content.template)) {
    issues.push("Only a service agreement or a retainer can be brought in.");
  }
  if (!content.client.name.trim()) issues.push("Add the client's name.");
  if (!draft.clientSignerName.trim()) issues.push("Add who signed for the client.");
  if (!draft.providerSignerName.trim()) issues.push("Add who signed for IDK Solutions.");
  if (!content.scopeItems.some((s) => s.trim())) issues.push("Add at least one thing the contract covers.");
  if (!isIsoDate(content.commencementDate)) issues.push("Add the start date.");
  if (!isIsoDate(draft.signedOn)) {
    issues.push("Add the date it was signed.");
  } else if (draft.signedOn > southAfricanDay(nowMs)) {
    issues.push("The signing date can't be in the future.");
  }
  if (content.template === "service") {
    if (content.paymentPlan === "phase_by_phase") {
      if (content.stages.length === 0) issues.push("Add the phases and what each one costs.");
      else if (content.stages.some((st) => !st.label.trim() || !(st.amount > 0))) {
        issues.push("Every phase needs a name and an amount.");
      }
    } else if (!(content.fee > 0)) {
      issues.push("Add the project fee.");
    }
  }
  if (content.template === "retainer") {
    if (!(content.monthlyFee > 0)) issues.push("Add the monthly fee.");
    if (!(content.minimumTermMonths >= 1)) issues.push("Add the minimum term in months.");
  }
  return issues;
};

/** "R1 250,50", "1250.5" or "1 250" as rand; 0 when it isn't an amount. */
export const parseRand = (text: string): number => {
  const cleaned = text.replace(/[R\s]/gi, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return 0;
  return Number(cleaned);
};

/**
 * Phases typed one per line: "Design, 5000", "Build - R7 000" or
 * "Launch | 3000". The amount is the last thing on the line.
 */
export const parseStages = (text: string): ContractStage[] =>
  text
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(.*?)[\s]*[,|:\-–]\s*(R?\s*[\d][\d\s.,]*)$/i.exec(line);
      return match
        ? { label: match[1].trim(), amount: parseRand(match[2]) }
        : { label: line, amount: 0 };
    });

export const stagesToText = (stages: readonly ContractStage[]): string =>
  stages.map((st) => `${st.label}, ${st.amount}`).join("\n");
